import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { Aircraft } from "../../../types";
import { AutocompleteAircraft } from "../../Settings/HangarSettings/AutocompleteAircraft";
import { AircraftSuggestion } from "./aircraftSuggestionUtils";
import { BulkApplyFloatingAction } from "./BulkApplyFloatingAction";

interface SuggestedAircraftCellProps {
  suggestion: AircraftSuggestion | null;
  currentMapping: Aircraft | null;
  onApprove: (aircraft: Aircraft) => Promise<void>;
  onManualChange: (aircraft: Aircraft) => Promise<void>;
  onBulkApplyRequest?: (aircraft: Aircraft) => void;
  bulkApplyContext?: {
    aircraft: Aircraft;
    matchCount: number;
    onApply: () => void;
    onDismiss: () => void;
  } | null;
}

/**
 * Enhanced autocomplete cell with intelligent aircraft suggestions.
 *
 * Shows three possible states:
 * 1. No suggestion: Standard empty autocomplete
 * 2. With suggestion: Prefilled autocomplete + checkmark button
 * 3. Already mapped: Shows current mapping
 */
export const SuggestedAircraftCell: React.FC<SuggestedAircraftCellProps> = ({
  suggestion,
  currentMapping,
  onApprove,
  onManualChange,
  onBulkApplyRequest,
  bulkApplyContext,
}) => {
  const [isApproving, setIsApproving] = React.useState(false);
  const [flashSuggestion, setFlashSuggestion] = React.useState(false);

  // Track which aircraft ID we've flashed to avoid re-flashing on filter changes
  const flashedAircraftId = React.useRef<string | null>(null);

  // Memoize defaultSelected to prevent new object references on every render
  // This prevents AutocompleteAircraft from thinking the value changed
  const defaultSelected = React.useMemo(() => {
    return currentMapping || suggestion?.suggestedAircraft || undefined;
  }, [currentMapping?.id, suggestion?.suggestedAircraft?.id]);

  // Show suggestion as flashing when it's a new suggestion (not already mapped)
  const shouldFlash = !currentMapping && suggestion?.confidence !== null && flashSuggestion;

  // Flash only when we get a NEW aircraft suggestion (different ID than previously flashed)
  React.useEffect(() => {
    const hasSuggestion = !currentMapping && suggestion?.confidence !== null && suggestion?.suggestedAircraft?.id;

    if (hasSuggestion) {
      const currentAircraftId = suggestion.suggestedAircraft.id;

      // Only flash if this is a different aircraft than we've already flashed
      if (flashedAircraftId.current !== currentAircraftId) {
        flashedAircraftId.current = currentAircraftId;
        setFlashSuggestion(true);
        const timer = setTimeout(() => setFlashSuggestion(false), 1500);
        return () => clearTimeout(timer);
      }
    } else if (currentMapping?.id) {
      // Reset the flashed ID when suggestion disappears (e.g., row gets mapped)
      flashedAircraftId.current = null;
    }
  }, [suggestion?.suggestedAircraft?.id, suggestion?.confidence, currentMapping?.id]);

  const handleApprove = async () => {
    if (!suggestion?.suggestedAircraft) return;

    setIsApproving(true);
    try {
      await onApprove(suggestion.suggestedAircraft);
    } catch (error) {
      console.error("Failed to approve suggestion:", error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleManualChange = async (aircraft: Aircraft) => {
    if (!aircraft) return;
    await onManualChange(aircraft);
    // Trigger bulk apply request when aircraft is selected
    if (onBulkApplyRequest) {
      onBulkApplyRequest(aircraft);
    }
  };

  // Determine checkmark color based on confidence
  const getCheckmarkColor = (): "success" | "warning" => {
    if (suggestion?.confidence === "high") return "success";
    if (suggestion?.confidence === "medium") return "warning";
    return "success"; // fallback
  };

  // Determine detailed tooltip content with score breakdown
  const getTooltipContent = (): React.ReactNode => {
    if (!suggestion || suggestion.confidence === null) return "";

    const { score, confidence, breakdown, suggestedAircraft } = suggestion;
    const confidenceLabel = confidence === "high" ? "High" : "Medium";
    const confidenceColor = confidence === "high" ? "success.main" : "warning.main";

    return (
      <Box sx={{ p: 1, maxWidth: 400 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Why "{suggestedAircraft?.make} {suggestedAircraft?.model}" was suggested:
        </Typography>

        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Score: {score.toFixed(1)}</strong>{" "}
          <Box
            component="span"
            sx={{ color: confidenceColor, fontWeight: 600 }}
          >
            ({confidenceLabel} confidence)
          </Box>
        </Typography>

        {breakdown && (
          <>
            <Typography
              variant="caption"
              sx={{ mb: 0.5, color: "text.secondary", display: "block" }}
            >
              Score Breakdown:
            </Typography>
            <Box
              component="ul"
              sx={{ pl: 2, my: 0.5, fontSize: "0.75rem", "& li": { mb: 0.25 } }}
            >
              <li>
                Manufacturer:{" "}
                {breakdown.manufacturerScore >= 0.9
                  ? `${(breakdown.manufacturerScore * 100).toFixed(0)}% match ✓`
                  : breakdown.manufacturerScore >= 0.7
                  ? `${(breakdown.manufacturerScore * 100).toFixed(0)}% match ~`
                  : "no match ✗"}
              </li>
              <li>
                Model score:{" "}
                {breakdown.modelScore === Infinity
                  ? "no direct match"
                  : breakdown.modelScore.toFixed(1)}
              </li>
              {breakdown.historicalScore < Infinity && (
                <li>
                  Historical: {breakdown.historicalScore.toFixed(1)}
                  {breakdown.matchedHistoricalString && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        pl: 2,
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    >
                      Similar to: "{breakdown.matchedHistoricalString}"
                    </Typography>
                  )}
                </li>
              )}
              {breakdown.manufacturerPenalty > 0.1 && (
                <li>
                  Manufacturer penalty: +{breakdown.manufacturerPenalty.toFixed(1)}
                </li>
              )}
            </Box>
            {breakdown.method === "csv-verified" && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  px: 1,
                  py: 0.5,
                  bgcolor: "success.light",
                  color: "success.dark",
                  borderRadius: 1,
                  fontWeight: 600,
                }}
              >
                ✓ Verified exact match from database
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
            >
              Match method: {breakdown.method}
            </Typography>
          </>
        )}

        <Divider sx={{ my: 1 }} />

        <Typography
          variant="body2"
          sx={{ fontStyle: "italic", color: "warning.main", fontSize: "0.75rem" }}
        >
          Click checkmark to approve, or manually select if incorrect
        </Typography>
      </Box>
    );
  };

  // Show checkmark button only if there's a suggestion and no current mapping
  const showCheckmark = !currentMapping && suggestion?.confidence !== null;

  return (
    <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AutocompleteAircraft
          label=""
          defaultSelected={defaultSelected}
          shouldFlash={shouldFlash}
          onSelect={handleManualChange}
        />
      </Box>

      {showCheckmark && (
        <Tooltip
          title={getTooltipContent()}
          placement="left"
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 450,
                bgcolor: "background.paper",
                color: "text.primary",
                border: 1,
                borderColor: "divider",
                boxShadow: 3,
              },
            },
          }}
        >
          <IconButton
            size="small"
            color={getCheckmarkColor()}
            onClick={handleApprove}
            disabled={isApproving}
            sx={{
              flexShrink: 0,
              "&:hover": {
                transform: "scale(1.1)",
              },
              transition: "transform 0.2s",
            }}
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Bulk Apply Floating Action */}
      {bulkApplyContext && (
        <BulkApplyFloatingAction
          aircraftName={`${bulkApplyContext.aircraft.make} ${bulkApplyContext.aircraft.model}`}
          matchCount={bulkApplyContext.matchCount}
          onApply={bulkApplyContext.onApply}
          onDismiss={bulkApplyContext.onDismiss}
        />
      )}
    </Box>
  );
};
